import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import type { Props as BaseProps } from 'feature-components/assessment-filters/base-filter';
import BaseFilter from 'feature-components/assessment-filters/base-filter';
import type { BaseGroup } from 'services/assist-api/entities/group/groups';
import { useGroups } from 'services/assist-api/entities/group/groups';
import { useAuthCustomer } from 'services/providers/auth.hooks';

export type Props<IsMulti extends boolean> = {
  enabledIds?: number[];
  filteredIds?: number[];
  label?: BaseProps<BaseGroup, IsMulti>['label'];
} & Pick<
  BaseProps<BaseGroup, IsMulti>,
  'isMulti' | 'isClearable' | 'isSearchable' | 'value' | 'onChange'
>;

/**
 * Filter that is auto populated by backend defined groups
 *
 *  - fetches organisation/customer groups
 *  - has loading state until groups are fetched
 *  - hides groups not included in `enabledIds`: this approach is taken as generally there are a lot of groups
 *  - disabled if there are no `enabledIds`
 */
export default function GroupFilter<IsMulti extends boolean = false>({
  enabledIds,
  label = 'Class',
  isClearable = true,
  isSearchable = true,
  ...props
}: Props<IsMulti>): ReactElement {
  const organisationId = useAuthCustomer()?.id || 0;

  const groups = useGroups({ filters: { customer: organisationId } }).data?.json;
  const options = useMemo(
    () => groups?.filter((g) => enabledIds?.includes(g.id)),
    [groups, enabledIds]
  );

  return (
    <BaseFilter<BaseGroup, IsMulti>
      label={label}
      options={options}
      getOptionValue={(o): string => String(o.id)}
      getOptionLabel={(o): string => o.name}
      isLoading={!groups?.length}
      isDisabled={!enabledIds?.length}
      isClearable={isClearable}
      isSearchable={isSearchable}
      {...props}
    />
  );
}
